import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Send, Users, Settings as Cog, Trash2, Plus, RefreshCw,
  CheckCircle2, AlertTriangle, Loader2,
} from "lucide-react";

export default function WhatsAppMarketing() {
  const [tab, setTab] = useState("broadcast");
  const [cfg, setCfg] = useState({ configured: false, phone_number_id: "", waba_id: "", default_language: "en_US", meta_token_present: false });
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [history, setHistory] = useState([]);

  // form state
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [defaultLang, setDefaultLang] = useState("en_US");
  const [listName, setListName] = useState("");
  const [recipientsRaw, setRecipientsRaw] = useState("");
  const [bcastTemplate, setBcastTemplate] = useState("");
  const [bcastLang, setBcastLang] = useState("en_US");
  const [bcastListId, setBcastListId] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [c, l, h, s] = await Promise.all([
        api.get("/whatsapp/config"),
        api.get("/whatsapp/lists"),
        api.get("/whatsapp/broadcasts"),
        api.get("/whatsapp/template-submissions"),
      ]);
      setCfg(c.data);
      setPhoneNumberId(c.data.phone_number_id || "");
      setWabaId(c.data.waba_id || "");
      setDefaultLang(c.data.default_language || "en_US");
      setLists(l.data);
      setHistory(h.data);
      setSubmissions(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const saveConfig = async () => {
    if (!phoneNumberId.trim() || !wabaId.trim()) return toast.error("Both IDs are required");
    setBusy(true);
    try {
      await api.put("/whatsapp/config", { phone_number_id: phoneNumberId.trim(), waba_id: wabaId.trim(), default_language: defaultLang });
      toast.success("WhatsApp settings saved");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const refreshTemplates = async () => {
    setTplLoading(true);
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data.templates || []);
      if (!data.templates?.length) toast.message("No approved templates yet", { description: "Create templates in WhatsApp Manager and wait for approval." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch templates");
    } finally { setTplLoading(false); }
  };

  const approvedTemplates = useMemo(() => templates.filter(t => (t.status || "").toUpperCase() === "APPROVED"), [templates]);

  const createList = async () => {
    if (!listName.trim()) return toast.error("Name required");
    const phones = recipientsRaw.split(/[\s,;\n]+/).map(x => x.trim()).filter(Boolean);
    if (!phones.length) return toast.error("Paste at least one phone number");
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/lists", { name: listName.trim(), recipients: phones });
      setLists([data, ...lists]);
      setListName(""); setRecipientsRaw("");
      toast.success(`List created with ${data.count} contacts`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create list");
    } finally { setBusy(false); }
  };

  const deleteList = async (id) => {
    try {
      await api.delete(`/whatsapp/lists/${id}`);
      setLists(lists.filter(l => l.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const sendBroadcast = async () => {
    if (!bcastTemplate) return toast.error("Pick a template");
    if (!bcastListId) return toast.error("Pick a contact list");
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/broadcast", { list_id: bcastListId, template_name: bcastTemplate, language_code: bcastLang });
      toast.success(`Broadcast complete: ${data.sent} sent, ${data.failed} failed`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="whatsapp-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">WhatsApp Marketing</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Broadcasts. Templates. Reach.</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Send approved WhatsApp Business templates to your contact lists at scale. All messages route through the official Cloud API.
        </p>
      </header>

      {/* status row */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5 flex items-center gap-3">
          {cfg.configured ? <CheckCircle2 className="h-6 w-6 text-green-400" /> : <AlertTriangle className="h-6 w-6 text-orange-400" />}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40">WA Config</div>
            <div className="text-sm">{cfg.configured ? "Phone ID + WABA saved" : "Not configured"}</div>
          </div>
        </div>
        <div className="glass rounded-2xl p-5 flex items-center gap-3">
          {cfg.meta_token_present ? <CheckCircle2 className="h-6 w-6 text-green-400" /> : <AlertTriangle className="h-6 w-6 text-orange-400" />}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40">Meta token</div>
            <div className="text-sm">{cfg.meta_token_present ? "Connected" : "Connect at Settings → Connections"}</div>
          </div>
        </div>
        <div className="glass rounded-2xl p-5 flex items-center gap-3">
          <MessageSquare className="h-6 w-6 text-orange-400" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40">Lists</div>
            <div className="text-sm">{lists.length} saved lists</div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: "broadcast", label: "Broadcast", Icon: Send },
          { id: "lists", label: "Contact Lists", Icon: Users },
          { id: "templates", label: "Templates", Icon: MessageSquare },
          { id: "settings", label: "Settings", Icon: Cog },
          { id: "history", label: "History", Icon: RefreshCw },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            data-testid={`wa-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${tab === id ? "brand-gradient" : "glass hover:bg-white/5"}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "settings" && (
        <div className="glass rounded-2xl p-6 lg:p-8 max-w-2xl space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Phone Number ID</label>
            <input
              data-testid="wa-phone-number-id"
              value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)}
              placeholder="e.g. 106540352242922"
              className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm font-mono"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">WhatsApp Business Account ID (WABA)</label>
            <input
              data-testid="wa-waba-id"
              value={wabaId} onChange={(e) => setWabaId(e.target.value)}
              placeholder="e.g. 102290129340398"
              className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm font-mono"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Default language</label>
            <select
              data-testid="wa-default-lang"
              value={defaultLang} onChange={(e) => setDefaultLang(e.target.value)}
              className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
            >
              {["en_US","en_GB","hi","mr","ta","te","bn","gu","kn","ml","pa","es","fr","de","ar","pt_BR"].map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <button
            data-testid="wa-save-config"
            onClick={saveConfig} disabled={busy}
            className="inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cog className="h-4 w-4" />}
            Save settings
          </button>
          <p className="text-xs text-white/40 leading-relaxed">
            Find these in your WhatsApp Manager → API Setup. Your access token comes from Meta OAuth (already connected at Settings → Connections).
          </p>
        </div>
      )}

      {tab === "templates" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm uppercase tracking-[0.2em] text-white/40">Your approved templates</h3>
            <button
              data-testid="wa-refresh-templates"
              onClick={refreshTemplates}
              disabled={tplLoading}
              className="glass px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 hover:bg-white/5 disabled:opacity-50"
            >
              {tplLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh from Meta
            </button>
          </div>
          {templates.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center text-sm text-white/50">
              No templates loaded. Click &quot;Refresh from Meta&quot; or create templates in WhatsApp Manager.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {templates.map((t, i) => (
                <div key={`${t.name}-${i}`} className="glass rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm">{t.name}</span>
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${t.status === "APPROVED" ? "bg-green-500/10 text-green-400" : "bg-orange-500/10 text-orange-400"}`}>
                      {t.status}
                    </span>
                  </div>
                  <div className="text-xs text-white/40">{t.category} · {t.language}</div>
                </div>
              ))}
            </div>
          )}

          <div className="pt-4 border-t border-white/5">
            <h3 className="text-sm uppercase tracking-[0.2em] text-white/40 mb-4">Submitted from AI Content Studio</h3>
            {submissions.length === 0 ? (
              <div className="glass rounded-2xl p-8 text-center text-sm text-white/50">
                No submissions yet. Go to <a href="/dashboard/content" className="underline text-green-400">/dashboard/content</a>, click the WhatsApp icon on any AI-generated post, and submit it as a template.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {submissions.map((s) => (
                  <div key={s.id} className="glass rounded-2xl p-5" data-testid={`wa-submission-${s.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm">{s.name}</span>
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${s.wa_status === "APPROVED" ? "bg-green-500/10 text-green-400" : s.wa_status === "REJECTED" ? "bg-red-500/10 text-red-400" : "bg-orange-500/10 text-orange-400"}`}>
                        {s.wa_status}
                      </span>
                    </div>
                    <div className="text-xs text-white/40">{s.category} · {s.language} · {s.source}</div>
                    <div className="text-xs text-white/60 mt-2 line-clamp-2">{s.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "lists" && (
        <div className="space-y-6">
          <div className="glass rounded-2xl p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-[0.2em] text-white/40">Create contact list</h3>
            <input
              data-testid="wa-list-name"
              value={listName} onChange={(e) => setListName(e.target.value)}
              placeholder="List name (e.g. 'Loyalty subscribers')"
              className="w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
            />
            <textarea
              data-testid="wa-list-recipients"
              value={recipientsRaw} onChange={(e) => setRecipientsRaw(e.target.value)}
              placeholder="Paste phone numbers separated by commas, spaces or newlines. Use E.164 (+919876543210)."
              rows={5}
              className="w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm font-mono"
            />
            <button
              data-testid="wa-create-list"
              onClick={createList} disabled={busy}
              className="inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> Create list
            </button>
          </div>

          {lists.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">No lists yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {lists.map((l) => (
                <div key={l.id} className="glass rounded-2xl p-5" data-testid={`wa-list-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{l.name}</div>
                      <div className="text-xs text-white/40">{l.count} contacts</div>
                    </div>
                    <button onClick={() => deleteList(l.id)} className="p-2 rounded-lg hover:bg-white/5">
                      <Trash2 className="h-4 w-4 text-white/50" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "broadcast" && (
        <div className="glass rounded-2xl p-6 lg:p-8 space-y-4 max-w-3xl">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Contact list</label>
              <select
                data-testid="wa-broadcast-list"
                value={bcastListId} onChange={(e) => setBcastListId(e.target.value)}
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
              >
                <option value="">— Select a list —</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.count})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Template</label>
              <div className="flex gap-2 mt-2">
                <select
                  data-testid="wa-broadcast-template"
                  value={bcastTemplate} onChange={(e) => setBcastTemplate(e.target.value)}
                  className="flex-1 bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
                >
                  <option value="">— Select a template —</option>
                  {approvedTemplates.map((t, i) => <option key={`${t.name}-${i}`} value={t.name}>{t.name} · {t.language}</option>)}
                </select>
                <button onClick={refreshTemplates} className="glass px-3 rounded-lg hover:bg-white/5" title="Refresh from Meta">
                  <RefreshCw className={`h-4 w-4 ${tplLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Language code</label>
              <input
                data-testid="wa-broadcast-lang"
                value={bcastLang} onChange={(e) => setBcastLang(e.target.value)}
                placeholder="en_US"
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm font-mono"
              />
            </div>
          </div>
          <button
            data-testid="wa-broadcast-send"
            onClick={sendBroadcast} disabled={busy}
            className="inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send broadcast
          </button>
          <p className="text-xs text-white/40">
            Each message is one Graph API call. WhatsApp rate-limits new numbers; expect ~80 msgs/sec at tier 1.
          </p>
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">No broadcasts yet.</div>
          ) : history.map(h => (
            <div key={h.id} className="glass rounded-2xl p-5 flex items-center justify-between" data-testid={`wa-history-${h.id}`}>
              <div>
                <div className="text-sm font-semibold">{h.template_name} <span className="text-white/40 font-mono text-xs">· {h.language_code}</span></div>
                <div className="text-xs text-white/40">{new Date(h.created_at).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className="text-sm"><span className="text-green-400">{h.sent ?? 0}</span> / <span className="text-red-400">{h.failed ?? 0}</span> / {h.recipients_count}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{h.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
